/**
 * useEmailPreview - Phase 10a.1 Email Trust & Test Foundation
 *
 * React Query hook for fetching email preview HTML.
 * Debounces customNote changes (500ms) to avoid excessive API calls.
 * Uses staleTime: 30000 (30s cache) and enabled prop to control fetching.
 */
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface EmailPreviewResponse {
  html: string;
  subject: string;
}

export function useEmailPreview(
  type: 'invoice' | 'proposal',
  id: string,
  customNote?: string,
  enabled: boolean = true
) {
  // Debounce customNote changes (500ms) while typing,
  // but flush immediately when enabled transitions to true (user clicks Preview)
  const [debouncedNote, setDebouncedNote] = useState(customNote);
  const [wasEnabled, setWasEnabled] = useState(enabled);

  useEffect(() => {
    // Flush debounce immediately when switching to preview mode
    if (enabled && !wasEnabled) {
      setDebouncedNote(customNote);
    }
    setWasEnabled(enabled);
  }, [enabled, wasEnabled, customNote]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedNote(customNote);
    }, 500);

    return () => clearTimeout(timer);
  }, [customNote]);

  const query = useQuery<EmailPreviewResponse>({
    queryKey: ['email-preview', type, id, debouncedNote],
    queryFn: async () => {
      const noteParam = debouncedNote
        ? `?custom_note=${encodeURIComponent(debouncedNote)}`
        : '';

      const endpoint =
        type === 'invoice'
          ? `/api/invoices/${id}/preview-email${noteParam}`
          : `/api/projects/${id}/preview-proposal-email${noteParam}`;

      const response = await api.get(endpoint);
      return response as EmailPreviewResponse;
    },
    staleTime: 30000,
    enabled: enabled && !!id,
  });

  return {
    html: query.data?.html,
    subject: query.data?.subject,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}
